import { ORDER_DETAILS_PAGE_SCAN_SUBMIT_INTENTS } from '../constants/orderDetailsPageScanSubmitIntents.const';
import { SUBMIT_ORDER_DETAILS_PAGE_ACTION_NAME } from '../constants/scanActionNames.const';
import { SCAN_SUBMIT_TARGET_REGISTRY } from '../constants/scanSubmitTargetRegistry.const';
import { SCAN_UI_CLOSE_TARGET_REGISTRY } from '../constants/scanUiCloseTargetRegistry.const';
import {
  type CreateSubmitOrderDetailsPageScanActionOptions,
  type OrderDetailsPageScanSubmitIntent,
} from '../types/orderDetailsPageScanSubmit.type';
import { type ScanAction, type ScanActionResult } from '../types/scanAction.type';
import { type ScanBarcodeContent } from '../types/scanContext.type';
import { type ScanEvent } from '../types/scanEvent.type';
import { type ScanSubmitTarget } from '../types/scanSubmitTarget.type';
import { matchesScannedOrderShipment } from '../utils/matchesScannedOrderShipment.util';
import { resolveOrderDetailsPageRoute } from '../utils/resolveOrderDetailsPageRoute.util';

const findTarget = (
  targets: readonly ScanSubmitTarget[],
  intent: OrderDetailsPageScanSubmitIntent,
  barcodeContent: ScanBarcodeContent,
): ScanSubmitTarget | undefined =>
  targets.find(
    (target) => target.intent === intent && matchesScannedOrderShipment(target, barcodeContent),
  );

export const createSubmitOrderDetailsPageScanAction = ({
  closeRegistry = SCAN_UI_CLOSE_TARGET_REGISTRY,
  registry = SCAN_SUBMIT_TARGET_REGISTRY,
}: CreateSubmitOrderDetailsPageScanActionOptions = {}): ScanAction => ({
  execute: (scanEvent: ScanEvent): ScanActionResult => {
    const barcodeContent = scanEvent.scanContext.barcodeContent;

    if (!barcodeContent) return { success: true };

    const targets = registry.snapshot();
    // Checkout keeps both intents registered, so buying wins over reopening.
    const target =
      findTarget(
        targets,
        ORDER_DETAILS_PAGE_SCAN_SUBMIT_INTENTS.COMPLETE_CHECKOUT,
        barcodeContent,
      ) ??
      findTarget(targets, ORDER_DETAILS_PAGE_SCAN_SUBMIT_INTENTS.START_CHECKOUT, barcodeContent);

    if (!target) return { success: true };

    if (target.isSubmitting) return { success: true };

    if (!target.canSubmit) {
      // Deliberately leaves sub-screens open here: a blocked purchase (e.g. an invalid
      // recipient address) often means the open sub-screen is exactly what the user
      // needs to fix the blocker, and there's no purchase popup to surface on top of it.
      if (target.blocker) target.onBlocked(target.blocker);

      return { success: true };
    }

    closeRegistry.closeAll();
    target.submit();

    return { success: true };
  },

  matches: (scanEvent: ScanEvent): boolean => {
    if (!scanEvent.scanContext.barcodeContent) return false;

    const route = resolveOrderDetailsPageRoute(scanEvent.scanContext.route.rawPath);

    if (!route) return false;

    return registry
      .snapshot()
      .some(
        (target) =>
          target.orderObjectId === route.orderObjectId &&
          target.batchEntryId === route.batchEntryId,
      );
  },

  name: SUBMIT_ORDER_DETAILS_PAGE_ACTION_NAME,
});
